/**
 * Column model for the Agent Runs table.
 *
 * Three groups:
 *   - FIXED LEADING  : always shown, always first, in this order → Status, Run ID
 *   - CONFIGURABLE   : user-toggleable middle columns (Edit Columns popup)
 *   - FIXED TRAILING : always shown, always last → Time (started_at)
 *
 * Fixed columns cannot be toggled off or reordered. Only the configurable
 * middle columns are persisted (per team) — the fixed columns are re-added
 * around them at render time by `resolveRunColumns`.
 */

export const ALL_RUN_COLUMNS = [
  // ── Fixed leading ──
  // Status is NOT its own column — it's shown as a colored dot to the left of
  // the Run ID value (green = ok, red = error, blue = running).
  { key: 'run_id',           label: 'Run ID',        default: true,  fixed: true },

  // ── Configurable middle (ON by default) ──
  { key: 'duration_ms',      label: 'Duration',      default: true },
  { key: 'distinct_id',      label: 'User',          default: true },
  { key: 'total_tokens_in',  label: 'Tokens in',     default: true },
  { key: 'total_tokens_out', label: 'Tokens out',    default: true },
  { key: 'total_cost',       label: 'Cost',          default: true },
  { key: 'rage_pct',         label: 'Rage %',        default: true },
  { key: 'sat_pct',          label: 'Sat %',         default: true },

  // ── Configurable middle (OFF by default) ──
  { key: 'agent_name',       label: 'Agent',         default: false },
  { key: 'span_count',       label: 'Spans',         default: false },
  { key: 'tool_count',       label: 'Tools',         default: false },
  { key: 'error_count',      label: 'Errors',        default: false },
  { key: 'feedback',         label: 'Feedback',      default: false },
  { key: 'cluster_id',       label: 'Cluster',       default: false },
  { key: 'conversation_id',  label: 'Conversation',  default: false },

  // ── Fixed trailing ──
  { key: 'started_at',       label: 'Time',          default: true,  fixed: true },
];

/** Fixed columns, in their pinned positions. */
export const FIXED_LEADING_KEYS = ['run_id'];
export const FIXED_TRAILING_KEYS = ['started_at'];
export const FIXED_RUN_COLUMN_KEYS = [...FIXED_LEADING_KEYS, ...FIXED_TRAILING_KEYS];

/** Toggleable columns shown in the Edit Columns popup (excludes fixed). */
export const CONFIGURABLE_RUN_COLUMNS = ALL_RUN_COLUMNS.filter((c) => !c.fixed);

/** Default ENABLED configurable keys — the middle defaults. */
export const DEFAULT_RUN_COLUMN_KEYS = CONFIGURABLE_RUN_COLUMNS
  .filter((c) => c.default)
  .map((c) => c.key);

/**
 * Resolve the full ordered list of column defs to render:
 *   [ ...fixed leading, ...enabled configurable (in order), ...fixed trailing ]
 * Fixed keys present in `enabledKeys` are ignored (they're added by position).
 */
export function resolveRunColumns(enabledKeys) {
  const byKey = new Map(ALL_RUN_COLUMNS.map((c) => [c.key, c]));
  const fixed = new Set(FIXED_RUN_COLUMN_KEYS);
  const middle = (Array.isArray(enabledKeys) ? enabledKeys : [])
    .filter((k) => byKey.has(k) && !fixed.has(k))
    .map((k) => byKey.get(k));
  const lead = FIXED_LEADING_KEYS.map((k) => byKey.get(k)).filter(Boolean);
  const trail = FIXED_TRAILING_KEYS.map((k) => byKey.get(k)).filter(Boolean);
  return [...lead, ...middle, ...trail];
}

const STORAGE_PREFIX = 'agent-runs:columns:';

export function loadRunColumnKeys(teamId) {
  if (!teamId) return [...DEFAULT_RUN_COLUMN_KEYS];
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + teamId);
    if (!raw) return [...DEFAULT_RUN_COLUMN_KEYS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [...DEFAULT_RUN_COLUMN_KEYS];
    }
    // Keep only valid, non-fixed keys (fixed columns are added by the resolver).
    const valid = new Set(CONFIGURABLE_RUN_COLUMNS.map((c) => c.key));
    return parsed.filter((k) => valid.has(k));
  } catch {
    return [...DEFAULT_RUN_COLUMN_KEYS];
  }
}

export function saveRunColumnKeys(teamId, keys) {
  if (!teamId) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + teamId, JSON.stringify(keys));
  } catch {
    /* ignore */
  }
}
